import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, AlertCircle, CheckCircle, Save } from 'lucide-react';
import NewBusinessSection from './NewBusinessSection';
import CorrectiveActionForm from './CorrectiveActionForm';
import API from '../services/api';
import { getAppPath } from '../utils/runtimeMode';

const MinutesWorkflow = ({ formData, setFormData, committeeMembers, setIsDraft, initialAgendaId, onAgendaChange }) => {
  const navigate = useNavigate();
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [quorum, setQuorum] = useState('');
  // Changed to boolean flags (checkbox) instead of yes/no radios
  const [adoptAgenda, setAdoptAgenda] = useState(false);
  const [adoptPreviousMinutes, setAdoptPreviousMinutes] = useState(false);
  const [agendas, setAgendas] = useState([]);
  const [selectedAgendaId, setSelectedAgendaId] = useState(initialAgendaId || '');
  const [agendasLoading, setAgendasLoading] = useState(false);
  const [agendasError, setAgendasError] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftSaveStatus, setDraftSaveStatus] = useState(null); // 'saved', 'error', or null
  const [newBusinessSections, setNewBusinessSections] = useState({
    advisorReport: false,
    inspectionReports: false,
    investigations: false,
    workerConcerns: false,
    training: false,
    procedures: false,
    feedback: false,
    attachments: false
  });
  const [correctiveActions, setCorrectiveActions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [minutesAttachments, setMinutesAttachments] = useState([]);
  const [newBusinessData, setNewBusinessData] = useState({
    workerConcernsText: '',
    trainingText: '',
    proceduresText: '',
    feedbackText: '',
    selectedInspectionIds: [],
    selectedInspectionSummaries: [],
    selectedIncidentIds: [],
    selectedIncidentSummaries: []
  });

  // Additional state for form fields that need to persist
  const [additionalItemsHasItems, setAdditionalItemsHasItems] = useState('no');
  const [additionalItemsText, setAdditionalItemsText] = useState('');
  const [adoptAgendaMovedBy, setAdoptAgendaMovedBy] = useState('');
  const [adoptPreviousMinutesMovedBy, setAdoptPreviousMinutesMovedBy] = useState('');
  const [previousMeetingMinutes, setPreviousMeetingMinutes] = useState('');
  const [adjournmentTime, setAdjournmentTime] = useState('');
  const [adjournmentDate, setAdjournmentDate] = useState('');
  const [nextMeetingDate, setNextMeetingDate] = useState('');
  const [nextMeetingTime, setNextMeetingTime] = useState('');
  const [nextMeetingLocation, setNextMeetingLocation] = useState('');
  const [acknowledgement, setAcknowledgement] = useState(false);
  const [isViewingFinalized, setIsViewingFinalized] = useState(false); // Read-only mode for finalized minutes

  // Load active employees for corrective action assignee dropdowns
  useEffect(() => {
    let cancelled = false;
    async function loadEmployees() {
      try {
        const res = await API.get('/api/users', { params: { role: 'EMPLOYEE' } });
        if (cancelled) return;
        if (res.data && res.data.success && Array.isArray(res.data.users)) {
          const mapped = res.data.users.map((u, idx) => ({
            id: u.email || `emp-${idx}`,
            name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || (u.email || ''),
            email: u.email,
            department: u.department || ''
          }));
          setEmployees(mapped);
        }
      } catch (err) {
        console.error('Failed to load employees for corrective actions', err);
      }
    }
    loadEmployees();
    return () => { cancelled = true; };
  }, []);

  const toggleMember = (memberId) => {
    setSelectedMembers(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const addCorrectiveAction = (section, context = {}) => {
    setCorrectiveActions(prev => ([
      ...prev,
      {
        section,
        description: '',
        assignedTo: '',
        targetDate: '',
        // Optional inspection context: id + building/location + category/date/time
        inspectionId: context.inspectionId || null,
        building: context.building || '',
        category: context.category || '',
        inspectionDate: context.inspectionDate || '',
        inspectionTime: context.inspectionTime || '',
        id: Date.now()
      }
    ]));
  };

  // Sync with URL when initialAgendaId prop changes
  useEffect(() => {
    if (initialAgendaId && initialAgendaId !== selectedAgendaId) {
      setSelectedAgendaId(initialAgendaId);
    }
  }, [initialAgendaId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load available committee agendas (meetings) from the backend so the
  // dropdown only contains real meetings from the database.
  useEffect(() => {
    let cancelled = false;
    async function loadAgendas() {
      setAgendasLoading(true);
      setAgendasError(null);
      try {
        const res = await API.get('/api/committeeMeetings/list');
        if (cancelled) return;

        if (!res.data || res.data.success !== true) {
          throw new Error(res.data?.error || 'Failed to load agendas');
        }

        const meetings = res.data.meetings || [];
        setAgendas(Array.isArray(meetings) ? meetings : []);
      } catch (e) {
        if (!cancelled) setAgendasError(e.message || 'Failed to load agendas');
      } finally {
        if (!cancelled) setAgendasLoading(false);
      }
    }
    loadAgendas();
    return () => { cancelled = true; };
  }, []);

  // Load saved draft when agenda is selected
  useEffect(() => {
    if (!selectedAgendaId) return;
    
    let cancelled = false;
    async function loadDraft() {
      try {
        console.log('[MINUTES] Loading draft for meeting:', selectedAgendaId);
        const res = await API.get(`/api/committeeMeetings/${encodeURIComponent(selectedAgendaId)}/minutes/draft`);
        if (cancelled) return;
        
        console.log('[MINUTES] Draft API response:', res.data);
        
        if (res.data && res.data.success && res.data.draft) {
          const { minutes, correctiveActions: savedActions } = res.data.draft;
          
          // Check if viewing finalized minutes (read-only mode)
          const finalized = res.data.isFinalized === true;
          setIsViewingFinalized(finalized);
          if (finalized) {
            console.log('[MINUTES] Viewing finalized minutes in read-only mode');
          }
          
          console.log('[MINUTES] Draft minutes data:', minutes);
          console.log('[MINUTES] Draft correctiveActions:', savedActions);
          
          // Restore form data
          if (minutes) {
            if (minutes.date || minutes.time) {
              setFormData(prev => ({
                ...prev,
                date: minutes.date || prev.date,
                time: minutes.time || prev.time
              }));
            }
            
            // Restore quorum
            if (typeof minutes.quorumMet === 'boolean') {
              setQuorum(minutes.quorumMet ? 'yes' : 'no');
            }
            
            // Restore adoption checkboxes
            if (typeof minutes.adoptAgenda === 'boolean') {
              setAdoptAgenda(minutes.adoptAgenda);
            }
            if (typeof minutes.adoptPreviousMinutes === 'boolean') {
              setAdoptPreviousMinutes(minutes.adoptPreviousMinutes);
            }
            
            // Restore selected members
            if (Array.isArray(minutes.selectedMembers)) {
              setSelectedMembers(minutes.selectedMembers);
            }
            
            // Restore additional agenda items
            if (minutes.additionalItemsHasItems) {
              setAdditionalItemsHasItems(minutes.additionalItemsHasItems);
            }
            if (minutes.additionalItemsText) {
              setAdditionalItemsText(minutes.additionalItemsText);
            }
            
            // Restore adoption movers
            if (minutes.adoptAgendaMovedBy) {
              setAdoptAgendaMovedBy(minutes.adoptAgendaMovedBy);
            }
            if (minutes.adoptPreviousMinutesMovedBy) {
              setAdoptPreviousMinutesMovedBy(minutes.adoptPreviousMinutesMovedBy);
            }
            
            // Restore previous meeting minutes selection
            if (minutes.previousMeetingMinutes) {
              setPreviousMeetingMinutes(minutes.previousMeetingMinutes);
            }
            
            // Restore adjournment info
            if (minutes.adjournmentTime) {
              setAdjournmentTime(minutes.adjournmentTime);
            }
            if (minutes.adjournmentDate) {
              setAdjournmentDate(minutes.adjournmentDate);
            }
            
            // Restore next meeting info
            if (minutes.nextMeetingDate) {
              setNextMeetingDate(minutes.nextMeetingDate);
            }
            if (minutes.nextMeetingTime) {
              setNextMeetingTime(minutes.nextMeetingTime);
            }
            if (minutes.nextMeetingLocation) {
              setNextMeetingLocation(minutes.nextMeetingLocation);
            }
            
            // Restore acknowledgement
            if (typeof minutes.acknowledgement === 'boolean') {
              setAcknowledgement(minutes.acknowledgement);
            }
            
            // Restore new business sections and data
            if (minutes.newBusiness) {
              const nb = minutes.newBusiness;
              setNewBusinessSections({
                advisorReport: false,
                inspectionReports: nb.inspectionEnabled || false,
                investigations: nb.incidentsEnabled || false,
                workerConcerns: nb.workerConcernsEnabled || false,
                training: nb.trainingEnabled || false,
                procedures: nb.proceduresEnabled || false,
                feedback: nb.feedbackEnabled || false,
                attachments: false
              });
              
              setNewBusinessData({
                workerConcernsText: nb.workerConcernsText || '',
                trainingText: nb.trainingText || '',
                proceduresText: nb.proceduresText || '',
                feedbackText: nb.feedbackText || '',
                selectedInspectionIds: (nb.inspections || []).map(i => i.id),
                selectedInspectionSummaries: nb.inspections || [],
                selectedIncidentIds: (nb.incidents || []).map(i => i.id),
                selectedIncidentSummaries: nb.incidents || []
              });
            }
          }
          
          // Restore corrective actions
          if (Array.isArray(savedActions) && savedActions.length > 0) {
            setCorrectiveActions(savedActions);
          }
          
          // Restore attachments - fetch details for each attachment ID
          const savedAttachments = res.data.draft.attachments;
          if (Array.isArray(savedAttachments) && savedAttachments.length > 0) {
            console.log('[MINUTES] Restoring attachments:', savedAttachments);
            // Fetch attachment details for each ID
            const attachmentDetails = await Promise.all(
              savedAttachments.map(async (attachmentId) => {
                try {
                  // Use the incidents attachment endpoint to get info
                  const attachRes = await API.get(`/api/incidents/attachments/${attachmentId}/info`);
                  if (attachRes.data && attachRes.data.success) {
                    return {
                      id: attachmentId,
                      url: `/api/incidents/attachments/${attachmentId}`,
                      fileName: attachRes.data.originalName || attachRes.data.fileName || 'Attachment'
                    };
                  }
                } catch (infoErr) {
                  console.warn('[MINUTES] Could not fetch attachment info:', attachmentId, infoErr.message);
                }
                // Return basic info if details couldn't be fetched
                return {
                  id: attachmentId,
                  url: `/api/incidents/attachments/${attachmentId}`,
                  fileName: 'Attachment'
                };
              })
            );
            setMinutesAttachments(attachmentDetails.filter(Boolean));
          }
          
          // Mark as draft
          if (setIsDraft) setIsDraft(true);
          
          console.log('[MINUTES] Loaded saved draft for meeting:', selectedAgendaId);
        }
      } catch (err) {
        // 404 means no draft exists - that's fine
        if (err?.response?.status !== 404) {
          console.error('Failed to load draft:', err);
        }
      }
    }
    
    loadDraft();
    return () => { cancelled = true; };
  }, [selectedAgendaId, setFormData, setIsDraft]);

  const selectedAgenda = agendas.find(a => String(a.ID || a.id) === String(selectedAgendaId));

  // Handler for saving draft
  const handleSaveDraft = async () => {
    if (!selectedAgendaId) {
      alert('Please select a committee agenda before saving the draft.');
      return;
    }

    setIsSavingDraft(true);
    setDraftSaveStatus(null);

    try {
      const draftData = {
        minutes: {
          date: formData.date || '',
          time: formData.time || '',
          quorumMet: quorum === 'yes',
          adoptAgenda,
          adoptPreviousMinutes,
          selectedMembers,
          // Additional fields for full state persistence
          additionalItemsHasItems,
          additionalItemsText,
          adoptAgendaMovedBy,
          adoptPreviousMinutesMovedBy,
          previousMeetingMinutes,
          adjournmentTime,
          adjournmentDate,
          nextMeetingDate,
          nextMeetingTime,
          nextMeetingLocation,
          acknowledgement,
          newBusiness: {
            inspectionEnabled: newBusinessSections.inspectionReports,
            inspections: newBusinessData.selectedInspectionSummaries || [],
            incidentsEnabled: newBusinessSections.investigations,
            incidents: newBusinessData.selectedIncidentSummaries || [],
            workerConcernsEnabled: newBusinessSections.workerConcerns,
            workerConcernsText: newBusinessData.workerConcernsText || '',
            trainingEnabled: newBusinessSections.training,
            trainingText: newBusinessData.trainingText || '',
            proceduresEnabled: newBusinessSections.procedures,
            proceduresText: newBusinessData.proceduresText || '',
            feedbackEnabled: newBusinessSections.feedback,
            feedbackText: newBusinessData.feedbackText || ''
          }
        },
        correctiveActions: correctiveActions,
        attachments: minutesAttachments.map(a => a.id)
      };

      await API.post(`/api/committeeMeetings/${encodeURIComponent(selectedAgendaId)}/minutes/draft`, draftData);
      
      setDraftSaveStatus('saved');
      if (setIsDraft) setIsDraft(true);
      
      // Clear the status after 3 seconds
      setTimeout(() => setDraftSaveStatus(null), 3000);
    } catch (err) {
      console.error('Failed to save draft:', err);
      const status = err?.response?.status;
      if (status === 440 || status === 401) {
        window.location.href = getAppPath('/login');
        return;
      }
      setDraftSaveStatus('error');
      setTimeout(() => setDraftSaveStatus(null), 5000);
    } finally {
      setIsSavingDraft(false);
    }
  };

  return (
    <div className="workflow-form">
      {/* Finalized minutes read-only banner */}
      {isViewingFinalized && (
        <div className="alert-banner alert-info" style={{ marginBottom: '16px', background: '#e3f2fd', borderColor: '#2196f3' }}>
          <FileText className="alert-icon" style={{ color: '#1976d2' }} />
          <div>
            <p className="alert-text" style={{ color: '#1565c0', fontWeight: 600 }}>
              You are viewing finalized minutes. This form is read-only.
            </p>
          </div>
        </div>
      )}
      {/* Draft save status banner */}
      {draftSaveStatus === 'saved' && (
        <div className="alert-banner alert-success" style={{ marginBottom: '16px' }}>
          <CheckCircle className="alert-icon" />
          <div>
            <p className="alert-text">Draft saved successfully!</p>
          </div>
        </div>
      )}
      {draftSaveStatus === 'error' && (
        <div className="alert-banner alert-warning" style={{ marginBottom: '16px' }}>
          <AlertCircle className="alert-icon" />
          <div>
            <p className="alert-text">Failed to save draft. Please try again.</p>
          </div>
        </div>
      )}

      <div className="alert-banner alert-success">
        <FileText className="alert-icon" />
        <div>
          <h3 className="alert-title">Committee Meeting Minutes</h3>
          <p className="alert-text">Document all meeting activities and decisions</p>
        </div>
      </div>

      <fieldset disabled={isViewingFinalized} style={{ border: 'none', padding: 0, margin: 0 }}>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Date</label>
          <input
            type="date"
            value={formData.date || ''}
            onChange={(e) => setFormData({...formData, date: e.target.value})}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Time (Update if needed)</label>
          <input
            type="time"
            value={formData.time || ''}
            onChange={(e) => setFormData({...formData, time: e.target.value})}
            className="form-input"
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Select Committee Agenda</label>
        <select
          className="form-select"
          value={selectedAgendaId}
          onChange={(e) => {
            const newId = e.target.value;
            setSelectedAgendaId(newId);
            if (onAgendaChange) onAgendaChange(newId);
          }}
        >
          <option value="">Select agenda</option>
          {agendasLoading && <option value="" disabled>Loading agendas…</option>}
          {!agendasLoading && agendasError && (
            <option value="" disabled>{agendasError}</option>
          )}
          {!agendasLoading && !agendasError && agendas.map(meeting => {
            const id = String(meeting.ID || meeting.id);
            const date = meeting.MEETING_DATE || meeting.meetingDate;
            const time = (meeting.MEETING_TIME || meeting.meetingTime || '').toString().substring(0,5);
            const loc = meeting.LOCATION || meeting.location || '';
            const label = `${date || 'Unknown date'} ${time || ''}${loc ? ` • ${loc}` : ''}`;
            return (
              <option key={id} value={id}>{label}</option>
            );
          })}
        </select>
        {/* Condensed summary of the selected agenda only: agenda link (if any)
            plus attendance counts. Date/time/location are already visible in
            the dropdown label above, so we avoid repeating them here. */}
        {selectedAgenda && (
          <div className="selected-agenda-summary" style={{ marginTop: '8px' }}>
            <div style={{ fontSize: '0.9rem' }}>
              <div>
                <strong>Agenda:</strong>{' '}
                {selectedAgenda.AGENDA_PDF_URL || selectedAgenda.agendaPdfUrl ? (
                  <a
                    href={selectedAgenda.AGENDA_PDF_URL || selectedAgenda.agendaPdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View PDF
                  </a>
                ) : (
                  <span style={{ color: '#666' }}>None</span>
                )}
              </div>
              <div style={{ marginTop: '4px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <span><strong>Attending:</strong> {selectedAgenda.ATTENDING_COUNT ?? selectedAgenda.attendingCount ?? 0}</span>
                <span><strong>Regrets:</strong> {selectedAgenda.REGRETS_COUNT ?? selectedAgenda.regretsCount ?? 0}</span>
                <span><strong>Pending:</strong> {selectedAgenda.PENDING_COUNT ?? selectedAgenda.pendingCount ?? 0}</span>
                <span><strong>Total:</strong> {selectedAgenda.TOTAL_INVITES ?? selectedAgenda.totalInvites ?? 0}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">Committee Members Participating</label>
        <div className="member-checkbox-grid">
          {committeeMembers.map(member => (
            <label key={member.id} className="member-checkbox">
              <input
                type="checkbox"
                checked={selectedMembers.includes(member.id)}
                onChange={() => toggleMember(member.id)}
                className="checkbox-input"
              />
              <span className="checkbox-label">
                <span className="member-name-bold">{member.name}</span>
                <span className="member-dept-text"> - {member.department}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="section-divider">
        <label className="form-label">
          <span className="required-mark">* </span>Quorum Confirmation
        </label>
        <div className="radio-group">
          <label className="radio-label">
            <input
              type="radio"
              name="quorum"
              value="yes"
              checked={quorum === 'yes'}
              onChange={(e) => setQuorum(e.target.value)}
              className="radio-input"
            />
            <span>Yes - Quorum Met</span>
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name="quorum"
              value="no"
              checked={quorum === 'no'}
              onChange={(e) => setQuorum(e.target.value)}
              className="radio-input"
            />
            <span>No - Quorum Not Met</span>
          </label>
        </div>
        {quorum === 'no' && (
          <div className="alert-banner alert-warning">
            <p className="alert-text-sm">
              ⚠️ The committee meeting does not meet quorum. The meeting can take place, 
              but members cannot vote on issues/concerns or decisions until quorum is met.
            </p>
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">Additional Agenda Items</label>
        <div className="radio-group">
          <label className="radio-label">
            <input 
              type="radio" 
              name="additionalItems" 
              value="no" 
              checked={additionalItemsHasItems === 'no'}
              onChange={(e) => setAdditionalItemsHasItems(e.target.value)}
              className="radio-input" 
            />
            <span>No</span>
          </label>
          <label className="radio-label">
            <input 
              type="radio" 
              name="additionalItems" 
              value="yes" 
              checked={additionalItemsHasItems === 'yes'}
              onChange={(e) => setAdditionalItemsHasItems(e.target.value)}
              className="radio-input" 
            />
            <span>Yes</span>
          </label>
        </div>
        <textarea
          placeholder="Enter additional agenda items from committee member responses..."
          className="form-textarea"
          rows="3"
          value={additionalItemsText}
          onChange={(e) => setAdditionalItemsText(e.target.value)}
        />
      </div>

      <div className="section-divider">
        <label className="form-label">
          <span className="required-mark">* </span>Adoption of Agenda
        </label>
        <select 
          className="form-select"
          value={adoptAgendaMovedBy}
          onChange={(e) => setAdoptAgendaMovedBy(e.target.value)}
        >
          <option value="">Select committee member who moved adoption</option>
          {committeeMembers.map(member => (
            <option key={member.id} value={member.id}>{member.name}</option>
          ))}
        </select>
        <div className="checkbox-group" style={{ marginTop: '8px' }}>
          <input
            type="checkbox"
            id="adoptAgendaCheckbox"
            className="checkbox-input"
            checked={adoptAgenda}
            onChange={(e) => setAdoptAgenda(e.target.checked)}
          />
          <label htmlFor="adoptAgendaCheckbox" className="checkbox-label">Adopted</label>
        </div>
      </div>

      <div className="section-divider">
        <label className="form-label">
          <span className="required-mark">* </span>Adoption of Previous Committee Minutes
        </label>
        <select 
          className="form-select"
          value={adoptPreviousMinutesMovedBy}
          onChange={(e) => setAdoptPreviousMinutesMovedBy(e.target.value)}
        >
          <option value="">Select committee member who moved adoption</option>
          {committeeMembers.map(member => (
            <option key={member.id} value={member.id}>{member.name}</option>
          ))}
        </select>
        <div className="checkbox-group" style={{ marginTop: '8px' }}>
          <input
            type="checkbox"
            id="adoptPrevMinutesCheckbox"
            className="checkbox-input"
            checked={adoptPreviousMinutes}
            onChange={(e) => setAdoptPreviousMinutes(e.target.checked)}
          />
          <label htmlFor="adoptPrevMinutesCheckbox" className="checkbox-label">Adopted</label>
        </div>
      </div>

      <div className="section-divider">
        <h3 className="section-title">Business Arising from Previous Minutes</h3>
        <select 
          className="form-select"
          value={previousMeetingMinutes}
          onChange={(e) => setPreviousMeetingMinutes(e.target.value)}
        >
          <option value="">Select previous meeting minutes</option>
        </select>
        {/* Previously this section rendered hard-coded sample items like
            "Item 1: Inspection follow-up for building A". Those examples
            have been removed to keep the minutes clean. When you're ready
            to wire this to real corrective actions or follow-ups from prior
            meetings, this area can be populated dynamically from data. */}
      </div>

      <div className="section-divider">
        <h3 className="section-title">Committee New Business</h3>
        
        <NewBusinessSection
          newBusinessSections={newBusinessSections}
          setNewBusinessSections={setNewBusinessSections}
          committeeMembers={committeeMembers}
          addCorrectiveAction={addCorrectiveAction}
          onAttachmentsChange={setMinutesAttachments}
          newBusinessData={newBusinessData}
          setNewBusinessData={setNewBusinessData}
          initialAttachments={minutesAttachments}
          readOnly={isViewingFinalized}
        />
      </div>

      {correctiveActions.length > 0 && (
        <div className="section-divider">
          <h3 className="section-title">Corrective Actions</h3>
          <div className="corrective-actions-list">
            {correctiveActions.map((action, idx) => (
              <CorrectiveActionForm
                key={action.id}
                action={action}
                idx={idx}
                committeeMembers={employees.length ? employees : committeeMembers}
                correctiveActions={correctiveActions}
                setCorrectiveActions={setCorrectiveActions}
              />
            ))}
          </div>
        </div>
      )}

      <div className="section-divider">
        <h3 className="section-title">Meeting Adjournment</h3>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Adjournment Time</label>
            <input 
              type="time" 
              className="form-input" 
              value={adjournmentTime}
              onChange={(e) => setAdjournmentTime(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Adjournment Date</label>
            <input 
              type="date" 
              className="form-input"
              value={adjournmentDate || formData.date || ''}
              onChange={(e) => setAdjournmentDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="section-divider">
        <h3 className="section-title">Next Meeting</h3>
        <div className="form-grid-three">
          <div className="form-group">
            <label className="form-label">Date</label>
            <input 
              type="date" 
              className="form-input"
              value={nextMeetingDate}
              onChange={(e) => setNextMeetingDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Time</label>
            <input 
              type="time" 
              className="form-input"
              value={nextMeetingTime}
              onChange={(e) => setNextMeetingTime(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Location</label>
            <input 
              type="text" 
              placeholder="Enter location" 
              className="form-input"
              value={nextMeetingLocation}
              onChange={(e) => setNextMeetingLocation(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="section-divider">
        <div className="alert-banner alert-warning">
          <AlertCircle className="alert-icon" />
          <div>
            <h3 className="alert-title">Final Acknowledgement</h3>
            <p className="alert-text">
              By signing off, you confirm that these meeting minutes are accurate and complete. 
              The minutes will be sent to all committee members and staff for reference.
            </p>
          </div>
        </div>
        
        <div className="checkbox-group">
          <input 
            type="checkbox" 
            id="acknowledge" 
            className="checkbox-input"
            checked={acknowledgement}
            onChange={(e) => setAcknowledgement(e.target.checked)}
          />
          <label htmlFor="acknowledge" className="checkbox-label">
            I acknowledge that the meeting minutes are accurate and complete
          </label>
        </div>
      </div>
      </fieldset>

      <div className="form-actions">
        {!isViewingFinalized && (
          <>
            <button 
              onClick={handleSaveDraft} 
              className="btn-secondary"
              disabled={isSavingDraft}
            >
              <Save className="btn-icon" />
              {isSavingDraft ? 'Saving...' : 'Save as Draft'}
            </button>
            <button
              className="btn-primary"
              disabled={!acknowledgement}
          onClick={async () => {
            if (!acknowledgement) {
              alert('Please acknowledge that the meeting minutes are accurate and complete before sending.');
              return;
            }
            // 1) Create inspection-related corrective actions and notify assignees.
            console.log('[MinutesWorkflow] All correctiveActions:', correctiveActions);
            const actionsToSend = correctiveActions.filter(a =>
              a.section === 'inspection' &&
              a.inspectionId &&
              a.description &&
              a.assignedTo &&
              a.targetDate
            );
            console.log('[MinutesWorkflow] actionsToSend (after filter):', actionsToSend);

            let createdActionIds = [];
            if (actionsToSend.length) {
              console.log('[MinutesWorkflow] Sending', actionsToSend.length, 'corrective action(s) to API...');
              try {
                const results = await Promise.all(
                  actionsToSend.map(a =>
                    API.post('/api/corrective-actions/from-minutes', {
                      inspectionId: a.inspectionId,
                      building: a.building || '',
                      category: a.category || '',
                      inspectionDate: a.inspectionDate || '',
                      inspectionTime: a.inspectionTime || '',
                      description: a.description,
                      assignedToEmail: a.assignedTo,
                      targetDate: a.targetDate
                    })
                  )
                );
                createdActionIds = results
                  .map(r => r.data && (r.data.actionId || r.data.id))
                  .filter(Boolean);
              } catch (err) {
                console.error('Failed to create corrective actions from minutes', err);
                const status = err?.response?.status;
                if (status === 440 || status === 401) {
                  window.location.href = getAppPath('/login');
                  return;
                }
                alert('Failed to send one or more corrective actions. Please try again.');
                return;
              }
            }

            // 2) Scaffold minutes finalize call to notify committee members.
            if (!selectedAgendaId) {
              alert('Please select a committee agenda before completing and sending minutes.');
              return;
            }

            try {
              await API.post(`/api/committeeMeetings/${encodeURIComponent(selectedAgendaId)}/minutes/finalize`, {
                minutes: {
                  date: formData.date || '',
                  time: formData.time || '',
                  quorumMet: quorum === 'yes',
                  adoptAgenda,
                  adoptPreviousMinutes,
                  selectedMembers,
                  // Additional fields for full state persistence
                  additionalItemsHasItems,
                  additionalItemsText,
                  adoptAgendaMovedBy,
                  adoptPreviousMinutesMovedBy,
                  previousMeetingMinutes,
                  adjournmentTime,
                  adjournmentDate,
                  nextMeetingDate,
                  nextMeetingTime,
                  nextMeetingLocation,
                  acknowledgement,
                  newBusiness: {
                    inspectionEnabled: newBusinessSections.inspectionReports,
                    inspections: newBusinessData.selectedInspectionSummaries || [],
                    incidentsEnabled: newBusinessSections.investigations,
                    incidents: newBusinessData.selectedIncidentSummaries || [],
                    workerConcernsEnabled: newBusinessSections.workerConcerns,
                    workerConcernsText: newBusinessData.workerConcernsText || '',
                    trainingEnabled: newBusinessSections.training,
                    trainingText: newBusinessData.trainingText || '',
                    proceduresEnabled: newBusinessSections.procedures,
                    proceduresText: newBusinessData.proceduresText || '',
                    feedbackEnabled: newBusinessSections.feedback,
                    feedbackText: newBusinessData.feedbackText || ''
                  }
                },
                attachments: minutesAttachments.map(a => a.id),
                correctiveActionIds: createdActionIds
              });
              // Navigate to confirmation page instead of showing alert
              navigate('/minutes-sent');
            } catch (err) {
              console.error('Failed to finalize minutes', err);
              const status = err?.response?.status;
              if (status === 440 || status === 401) {
                window.location.href = getAppPath('/login');
                return;
              }
              alert('Failed to finalize minutes. Please try again.');
            }
          }}
        >
          <CheckCircle className="btn-icon" />
          Complete & Send Minutes
        </button>
          </>
        )}
        {isViewingFinalized && (
          <p style={{ color: '#666', fontStyle: 'italic' }}>
            These minutes have been finalized and cannot be edited.
          </p>
        )}
      </div>
    </div>
  );
};

export default MinutesWorkflow;